/**
 * ParallaxScroll — scroll-driven parallax for split-image sections.
 *
 * Usage (from Liquid):
 *   new ParallaxScroll('parallax-desktop', { axis: 'vertical' });
 *   new ParallaxScroll('parallax-mobile',  { axis: 'horizontal' });
 *
 * Requires Motion (window.Motion.scroll / window.Motion.mix).
 */
class ParallaxScroll {
  /**
   * @param {string} containerId  — id of the outer scroll container
   * @param {Object} options
   * @param {'vertical'|'horizontal'} options.axis — scroll direction
   */
  constructor(containerId, { axis = 'vertical' } = {}) {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    this.areaOne = this.container.querySelector('[data-parallax="area-one"]');
    this.areaTwo = this.container.querySelector('[data-parallax="area-two"]');
    this.inner = this.container.querySelector('[data-parallax="inner"]');

    if (!this.areaOne || !this.areaTwo || !this.inner) return;

    this.axis = axis;
    this.isHorizontal = axis === 'horizontal';
    this.abortController = new AbortController();

    this._init();
  }

  _init() {
    const signal = this.abortController.signal;

    // Set initial layout properties once (not per-frame).
    if (this.isHorizontal) {
      this.inner.style.overflow = 'hidden';
      this.areaTwo.style.position = 'absolute';
      this._updateBannerHeight();
    }

    // Throttled resize via rAF.
    let resizeQueued = false;
    window.addEventListener(
      'resize',
      () => {
        if (resizeQueued) return;
        resizeQueued = true;
        requestAnimationFrame(() => {
          resizeQueued = false;
          this._updateBannerHeight();
          this._rebuildMixers();
        });
      },
      { signal },
    );

    // Run initial sizing synchronously — safe regardless of readyState
    // because we already have the container in the DOM by construction.
    this._updateBannerHeight();
    this._rebuildMixers();

    // Scroll-driven animation with rAF throttle tied directly to sticky scroll.
    this._onScroll = this._updateScrollPosition.bind(this);
    window.addEventListener('scroll', this._onScroll, { signal, passive: true });
    this._updateScrollPosition();

    // Teardown on Shopify section events.
    document.addEventListener(
      'shopify:section:unload',
      (e) => {
        if (this.container.closest(`[data-shopify-section-id="${e.detail.sectionId}"]`)) {
          this.destroy();
        }
      },
      { signal },
    );
  }

  /** Precompute mix functions so we don't create closures per frame. */
  _rebuildMixers() {
    const { mix } = window.Motion;

    if (this.isHorizontal) {
      const distance = window.innerWidth;
      this._mixOne = mix(0, -distance);
      this._mixTwo = mix(-distance, 0);
      this._maxTravel = 2 * distance;
    } else {
      const distance = this.inner ? this.inner.offsetHeight : this.areaOne.clientHeight / 2;
      this._mixOne = mix(0, -distance);
      this._mixTwo = mix(-distance, 0);
    }
  }

  _updateBannerHeight() {
    if (!this.isHorizontal) return;
    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10,
      ) || 56;
    const h = (window.innerHeight - headerHeight) / 2;
    this.container.style.setProperty('--banner-height-mobile', `${h}px`);
  }

  _updateScrollPosition() {
    if (this.isHorizontal) {
      const track =
        this.container.querySelector('.parallax-section__scroll-track--horizontal') ||
        this.container;
      const sticky = this.container.querySelector('.parallax-section__sticky--mobile');
      if (!track || !sticky) return;

      const trackRect = track.getBoundingClientRect();
      const stickyRect = sticky.getBoundingClientRect();
      const maxScroll = track.offsetHeight - sticky.offsetHeight;
      const currentScroll = stickyRect.top - trackRect.top;
      const progress = Math.min(Math.max(currentScroll / (maxScroll || 1), 0), 1);

      this.areaOne.style.transform = `translateX(${this._mixOne(progress)}px)`;
      this.areaTwo.style.transform = `translateX(${this._mixTwo(progress)}px)`;
    } else {
      const track =
        this.container.querySelector('.parallax-section__scroll-track--vertical') || this.container;
      const sticky = this.container.querySelector('.parallax-section__sticky');
      if (!track || !sticky) return;

      const trackRect = track.getBoundingClientRect();
      const stickyRect = sticky.getBoundingClientRect();
      const maxScroll = track.offsetHeight - sticky.offsetHeight;
      const currentScroll = stickyRect.top - trackRect.top;
      const progress = Math.min(Math.max(currentScroll / (maxScroll || 1), 0), 1);

      this.areaOne.style.transform = `translateY(${this._mixOne(progress)}px)`;
      this.areaTwo.style.transform = `translateY(${this._mixTwo(progress)}px)`;
    }
  }

  /** Clean up all listeners and scroll tracking. */
  destroy() {
    this.abortController.abort();
    if (typeof this._stopScroll === 'function') {
      this._stopScroll();
    }
  }
}

window.ParallaxScroll = ParallaxScroll;
